// Copyright Prashant Vaghela, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import { FC, useMemo, useState } from 'react';
import React from 'react';
import { BaseEdge, EdgeLabelRenderer, EdgeProps, getBezierPath, getSmoothStepPath, getStraightPath } from 'reactflow';

import { Pagination, Table, TableProps } from '@cloudscape-design/components';

// --- Type Definitions for EdgeLabel ---
interface TableItem {
  process: string;
  time: string;
}

interface EdgeLabelProps {
  transform: string;
  label: string | undefined;
  tableData: TableItem[] | undefined;
}

const columns: TableProps.ColumnDefinition<TableItem>[] = [
  {
    header: 'Process',
    cell: item => item.process,
    id: 'process',
  },
  {
    header: 'Time',
    cell: item => item.time,
    id: 'time',
  },
];

function EdgeLabel({ transform, label, tableData }: EdgeLabelProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;

  const handlePageChange = (pageIndex: number) => {
    setCurrentPage(pageIndex);
  };

  const totalItems = tableData?.length || 0;

  const currentData = tableData ? tableData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage) : [];

  return (
    <div
      style={{
        position: 'absolute',
        background: 'white',
        padding: 2,
        borderRadius: 5,
        fontSize: 12,
        fontWeight: 700,
        transform,
        border: '1px solid #ccc',
        width: 305,
        maxHeight: 220,
        pointerEvents: 'all',
        cursor: 'cell',
      }}
      className="nodrag nopan"
    >
      {label ? (
        label
      ) : (
        <>
          <Table
            columnDefinitions={columns}
            items={currentData}
            variant="embedded"
            resizableColumns
            stripedRows
            empty={<div style={{ textAlign: 'center' }}>No data available</div>}
          />
          <Pagination
            currentPageIndex={currentPage}
            onChange={({ detail }) => handlePageChange(detail.currentPageIndex)}
            pagesCount={Math.ceil(totalItems / itemsPerPage)}
            ariaLabels={{
              nextPageLabel: 'Next page',
              previousPageLabel: 'Previous page',
              pageLabel: pageNumber => `Page ${pageNumber} of all pages`,
            }}
            // size="small"
            // variant="buttons"
          />
        </>
      )}
    </div>
  );
}

// --- Type Definitions for CustomEdge ---
interface EdgeData {
  type: 'smoothstep' | 'bezier' | 'straight';
  strokeColor?: number; // duration is used to determine color
  endLabel?: string | TableItem[];
  startLabel?: string | TableItem[];
  item: { duration: number }; // required for strokeColor logic
  isAnimationOn?: boolean;
}

const CustomEdge: FC<EdgeProps<EdgeData>> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}) => {
  // Fallback for data.type if undefined
  const edgeType = data?.type || 'smoothstep';

  const [edgePath] = useMemo(() => {
    switch (edgeType) {
      case 'smoothstep':
        return getSmoothStepPath({
          sourceX,
          sourceY,
          sourcePosition,
          targetX,
          targetY,
          targetPosition,
        });
      case 'bezier':
        return getBezierPath({
          sourceX,
          sourceY,
          sourcePosition,
          targetX,
          targetY,
          targetPosition,
        });
      case 'straight':
        return getStraightPath({
          sourceX,
          sourceY,
          sourcePosition,
          targetX,
          targetY,
          targetPosition,
        });
      default:
        // Fallback for default smoothstep
        return getSmoothStepPath({
          sourceX,
          sourceY,
          sourcePosition,
          targetX,
          targetY,
          targetPosition,
        });
    }
  }, [
    edgeType, // Depend on local edgeType which defaults if data.type is missing
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  ]);

  const midPointX = (sourceX + targetX) / 2;
  // const midPointY = (sourceY + targetY) / 2; // unused

  // Determine stroke color based on duration in data.item
  const strokeColor = (data?.item?.duration || 0) > 0 ? 'red' : 'green';
  const strokeWidth = 10;

  // Transforms for positioning the EdgeLabels
  const startLabelTransform =
    sourcePosition === 'bottom'
      ? `translate(10%, 20%) translate(${midPointX}px,${sourceY}px)`
      : `translate(10%, 20%) translate(${midPointX}px,${sourceY}px)`;

  const endLabelTransform =
    sourcePosition === 'bottom'
      ? `translate(-50%, -100%) translate(${targetX + 180}px,${targetY - 10}px)`
      : `translate(-50%, -100%) translate(${targetX + 120}px, ${targetY - 120}px)`;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd="logo"
        style={{
          stroke: strokeColor,
          strokeWidth,
        }}
      />
      {data?.isAnimationOn && (
        <circle r="18" fill="green" stroke="lightgreen">
          {/* Note: In ReactFlow, paths are usually in screen space, so `path` is fine here */}
          <animateMotion dur="2s" repeatCount="indefinite" path={edgePath} />
        </circle>
      )}
      <EdgeLabelRenderer>
        {data?.endLabel && (
          <EdgeLabel
            transform={endLabelTransform}
            label={typeof data.endLabel === 'string' ? data.endLabel : undefined}
            tableData={Array.isArray(data.endLabel) ? data.endLabel : undefined}
          />
        )}
        {data?.startLabel && (
          <EdgeLabel
            transform={startLabelTransform}
            label={typeof data.startLabel === 'string' ? data.startLabel : undefined}
            tableData={Array.isArray(data.startLabel) ? data.startLabel : undefined}
          />
        )}
      </EdgeLabelRenderer>
    </>
  );
};

export default CustomEdge;
